// Trends (/trends): what Pulse readers open, click through and save, by period,
// topic and approximate country. Built by the Admin rebuild from the anonymous
// counters (shared/clicks.ts) into one public document, meta/trends, so a visit
// costs one read. Periods compare with the period just before (today vs
// yesterday, last 7 days vs the 7 before, last 30 vs the 30 before).
import type { ContentType, Topic } from "./model.ts";
import { CLICK_KINDS, type ClickKind } from "./clicks.ts";

export type TrendRange = "today" | "week" | "month" | "all";
export const TREND_RANGES: readonly TrendRange[] = ["today", "week", "month", "all"];
export const TREND_RANGE_LABELS: Record<TrendRange, string> = { today: "Today", week: "This week", month: "This month", all: "All time" };
const RANGE_DAYS: Record<Exclude<TrendRange, "all">, number> = { today: 1, week: 7, month: 30 };
export const TREND_TOP_STORIES = 10;
export const TREND_DAILY_DAYS = 30;

export type Counts = Record<ClickKind, number>;
export const zeroCounts = (): Counts => ({ opens: 0, sources: 0, saves: 0 });

export interface TrendStory extends Counts {
  storyId: string;
  title: string;
  topic: Topic;
  type: ContentType;
}
export interface TrendTopic {
  topic: Topic;
  now: Counts;
  /** Null for All time (nothing to compare with). */
  prev: Counts | null;
}
export interface TrendCountry {
  /** ISO 3166 alpha-2, from the reader's device time zone (approximate). */
  country: string;
  now: Counts;
  prev: Counts | null;
}
export interface TrendPeriod {
  /** UTC day numbers, inclusive; null for All time. */
  fromDay: number | null;
  toDay: number;
  totals: Counts;
  prevTotals: Counts | null;
  topics: TrendTopic[];
  /** Top stories for each counter. */
  stories: Record<ClickKind, TrendStory[]>;
  /** Every country with activity, most active first. */
  countries: TrendCountry[];
  /** Activity whose country couldn't be told (UTC time zone, blocked). */
  unknownCountry: Counts;
}
export interface TrendsDoc {
  generatedAt: number;
  today: number;
  periods: Record<TrendRange, TrendPeriod>;
  /** Totals per UTC day, oldest first, for the last 30 days (zeros included). */
  daily: (Counts & { day: number })[];
  /** True for the bundled fictional data used without a database. */
  isSample?: boolean;
}

export interface StoryDayRow extends Partial<Counts> {
  storyId: string;
  day: number;
}
export interface StoryTotalRow extends Partial<Counts> {
  storyId: string;
}
export interface CountryDayRow extends Partial<Counts> {
  country: string;
  day: number;
}
export interface TrendStoryMeta {
  title: string;
  topic: Topic;
  type: ContentType;
}

export const UNKNOWN_COUNTRY = "ZZ";

function add(into: Counts, row: Partial<Counts>): Counts {
  for (const k of CLICK_KINDS) into[k] += row[k] ?? 0;
  return into;
}
export const activity = (c: Counts) => c.opens + c.sources + c.saves;

/** Change from the previous period: a ratio (0.25 = +25%), "new" when it was zero, or null with nothing to compare. */
export function change(now: number, prev: number | null | undefined): number | "new" | null {
  if (prev === null || prev === undefined) return null;
  if (prev === 0) return now > 0 ? "new" : 0;
  return (now - prev) / prev;
}

function sumBy<T extends { day?: number }>(rows: T[], key: (r: T) => string | null, from: number | null, to: number): Map<string, Counts> {
  const out = new Map<string, Counts>();
  for (const r of rows) {
    if (from !== null && (r.day === undefined || r.day < from || r.day > to)) continue;
    const k = key(r);
    if (k === null) continue;
    add(out.get(k) ?? out.set(k, zeroCounts()).get(k)!, r as Partial<Counts>);
  }
  return out;
}

export function buildTrends(input: {
  storyDays: StoryDayRow[];
  storyTotals: StoryTotalRow[];
  countryDays: CountryDayRow[];
  stories: Map<string, TrendStoryMeta>;
  now: number;
}): TrendsDoc {
  const today = Math.floor(input.now / 86_400_000);
  const known = (id: string) => (input.stories.has(id) ? id : null);

  const period = (range: TrendRange): TrendPeriod => {
    const span = range === "all" ? null : RANGE_DAYS[range];
    const from = span === null ? null : today - span + 1;
    const prevFrom = span === null ? null : today - 2 * span + 1;
    const byStory =
      span === null
        ? sumBy(input.storyTotals.map((r) => ({ ...r, day: undefined })), (r) => known(r.storyId), null, today)
        : sumBy(input.storyDays, (r) => known(r.storyId), from, today);
    const prevByStory = span === null ? null : sumBy(input.storyDays, (r) => known(r.storyId), prevFrom, from! - 1);

    const topicOf = (m: Map<string, Counts>) => {
      const t = new Map<Topic, Counts>();
      for (const [id, c] of m) {
        const topic = input.stories.get(id)!.topic;
        add(t.get(topic) ?? t.set(topic, zeroCounts()).get(topic)!, c);
      }
      return t;
    };
    const topicsNow = topicOf(byStory);
    const topicsPrev = prevByStory && topicOf(prevByStory);
    const topics = [...new Set([...topicsNow.keys(), ...(topicsPrev?.keys() ?? [])])]
      .map((topic) => ({ topic, now: topicsNow.get(topic) ?? zeroCounts(), prev: topicsPrev ? (topicsPrev.get(topic) ?? zeroCounts()) : null }))
      .sort((a, b) => activity(b.now) - activity(a.now) || a.topic.localeCompare(b.topic));

    const stories = Object.fromEntries(
      CLICK_KINDS.map((k) => [
        k,
        [...byStory]
          .filter(([, c]) => c[k] > 0)
          .sort(([ia, a], [ib, b]) => b[k] - a[k] || activity(b) - activity(a) || ia.localeCompare(ib))
          .slice(0, TREND_TOP_STORIES)
          .map(([storyId, c]) => ({ storyId, ...input.stories.get(storyId)!, ...c })),
      ]),
    ) as Record<ClickKind, TrendStory[]>;

    const countriesNow = sumBy(input.countryDays, (r) => r.country, from, today);
    const countriesPrev = span === null ? null : sumBy(input.countryDays, (r) => r.country, prevFrom, from! - 1);
    const unknownCountry = countriesNow.get(UNKNOWN_COUNTRY) ?? zeroCounts();
    const countries = [...countriesNow]
      .filter(([cc]) => cc !== UNKNOWN_COUNTRY)
      .map(([country, now]) => ({ country, now, prev: countriesPrev ? (countriesPrev.get(country) ?? zeroCounts()) : null }))
      .sort((a, b) => activity(b.now) - activity(a.now) || a.country.localeCompare(b.country));

    const totals = [...byStory.values()].reduce(add, zeroCounts());
    const prevTotals = prevByStory ? [...prevByStory.values()].reduce(add, zeroCounts()) : null;
    return { fromDay: from, toDay: today, totals, prevTotals, topics, stories, countries, unknownCountry };
  };

  const perDay = sumBy(input.storyDays, (r) => (known(r.storyId) ? String(r.day) : null), today - TREND_DAILY_DAYS + 1, today);
  const daily = Array.from({ length: TREND_DAILY_DAYS }, (_, i) => {
    const day = today - TREND_DAILY_DAYS + 1 + i;
    return { day, ...(perDay.get(String(day)) ?? zeroCounts()) };
  });

  return {
    generatedAt: input.now,
    today,
    periods: Object.fromEntries(TREND_RANGES.map((r) => [r, period(r)])) as Record<TrendRange, TrendPeriod>,
    daily,
  };
}
